import {
    waiting_for_prompt_and_teacher,
    waiting_for_data,
    gathering_data,
    ready_for_distillation,
} from '@/shared/constants/taskStatuses';
import { normalizeWizardStep, wizardStepsFor } from '@/features/tasks/components/CreationWizard/WizardStepper';

/**
 * Local registry of "draft" tasks — tasks created by the creation wizard
 * (`/create-task`) but whose wizard flow was exited before training started.
 *
 * The wizard writes the current step here on every step change and removes
 * the entry when training kicks off, so the Projects page can render the
 * task as a draft card and clicking it reopens the wizard at the exact step
 * the user left.
 *
 * Stored in localStorage (per browser) because the backend has no draft
 * concept: the usecase itself is fully persisted server-side from step 1 on.
 */
const STORAGE_KEY = 'specificai.taskDrafts.v1';

/**
 * Statuses a task can have while still inside the creation wizard. Once a
 * task progresses past these (training started, deployed, ...) a leftover
 * registry entry no longer marks it as a draft.
 */
const SETUP_STATUSES = new Set([
    waiting_for_prompt_and_teacher,
    waiting_for_data,
    gathering_data,
    ready_for_distillation,
]);

const readRegistry = () => {
    try {
        const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY));
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
};

const writeRegistry = (registry) => {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(registry));
    } catch {
        // Storage unavailable (private mode / quota) — drafts just aren't remembered.
    }
};

/** Record (or refresh) the wizard step a draft task was left on. */
export const saveTaskDraft = (usecaseId, step) => {
    if (!usecaseId || !step) return;
    const registry = readRegistry();
    registry[usecaseId] = { step, updatedAt: Date.now() };
    writeRegistry(registry);
};

/** Forget a draft (wizard finished, or the task no longer qualifies). */
export const removeTaskDraft = (usecaseId) => {
    if (!usecaseId) return;
    const registry = readRegistry();
    if (!(usecaseId in registry)) return;
    delete registry[usecaseId];
    writeRegistry(registry);
};

/**
 * Whether the task progressed past the creation wizard (training started,
 * deployed, ...). A missing status still counts as in-setup: without a
 * status we cannot prove the wizard was finished.
 */
export const isPastWizardSetup = (usecase) =>
    Boolean(usecase?.status && !SETUP_STATUSES.has(usecase.status));

/**
 * Draft entry (`{ step, updatedAt }`) for a usecase row, or `null` when the
 * task is not a draft. A registry entry is ignored once the task progressed
 * past the setup statuses (e.g. training was started outside the wizard).
 */
export const getTaskDraft = (usecase) => {
    if (!usecase?._id) return null;
    const entry = readRegistry()[usecase._id];
    if (!entry?.step) return null;
    if (isPastWizardSetup(usecase)) return null;
    return entry;
};

/** Wizard URL that resumes a draft task at its saved step. */
export const draftWizardUrl = (usecaseId, step) => {
    const params = new URLSearchParams({ step, usecaseId });
    return `/create-task?${params.toString()}`;
};

/**
 * Labels of the wizard steps a draft has not completed yet — the saved step
 * and everything after it. Shown on the draft card as "Steps missing: ...".
 * Agentic tasks (`autonomy_level` ≠ 1) include the Agent Config step.
 */
export const getDraftMissingStepLabels = (draft, autonomyLevel = null) => {
    const steps = wizardStepsFor(autonomyLevel);
    // A draft saved on the removed Data Editor or Train step is Model Setup.
    const index = steps.findIndex((s) => s.key === normalizeWizardStep(draft?.step));
    const missing = index >= 0 ? steps.slice(index) : steps;
    return missing.map((s) => s.label);
};
